import classNames from 'classnames';
import React from 'react';

import Icon from 'Views/Components/_HumanWritten/Icon/Icon';

interface StopSeriesVisualiserProps {
	numberOfStops?: number;
	variant?: 'white' | 'contrast';
	/**
	 * Hide stop indicators below the number of stop label.
	 */
	intermediateStopsDisabled?: boolean;
	hideStopCountLabel?: boolean;
	/**
	 * Draw the two ends with Material Symbols rather than the app's own icon font.
	 *
	 * <p>Off by default, so the check-in stop card and the old ticket card keep the glyphs they
	 * were drawn with. The redesigned ticket card turns it on: everything else on that card — the
	 * capacities, the notes, the carets — is already a Material Symbol, and a custom-font ship
	 * between two of them is the one icon that does not match.</p>
	 */
	useMaterialIcons?: boolean;
}

export default function StopSeriesVisualiser({
	numberOfStops,
	variant = 'contrast',
	intermediateStopsDisabled = false,
	hideStopCountLabel = false,
	useMaterialIcons = false,
}: StopSeriesVisualiserProps) {
	const showIntermediateStops = React.useMemo(() => {
		return !intermediateStopsDisabled && !!numberOfStops && numberOfStops > 0 && numberOfStops < 10;
	}, [intermediateStopsDisabled, numberOfStops]);

	return (
		<div className="leg-option__stops-container">
			{!hideStopCountLabel && !!numberOfStops && (
				// Show text label when there is at least one stop
				<div className="leg-option__number-of-stops">
					{numberOfStops} stop{numberOfStops > 1 && 's'}
				</div>
			)}
			<div className="leg-option__stop-series-container">
				<StopDepartureIndicator variant={variant} useMaterialIcons={useMaterialIcons} />
				{showIntermediateStops && !!numberOfStops && (
					<>
						<RouteLine variant={variant} />
						<StopOutlineIndicator variant={variant} />
						{numberOfStops > 1 && (
							<>
								{Array.from({ length: numberOfStops - 1 }, (_, index) => (
									<React.Fragment key={index}>
										<RouteLine variant={variant} isSegment />
										<StopOutlineIndicator variant={variant} />
									</React.Fragment>
								))}
							</>
						)}
					</>
				)}
				<RouteLine variant={variant} />
				<StopDestinationIndicator variant={variant} useMaterialIcons={useMaterialIcons} />
			</div>
		</div>
	);
}

interface StopIndicatorProps {
	variant?: 'white' | 'contrast';
	useMaterialIcons?: boolean;
}

function StopOutlineIndicator({ variant }: StopIndicatorProps) {
	return (
		<div
			className={classNames('leg-option__stop-indicator', {
				'leg-option__stop-indicator--outline-white': variant === 'white',
				'leg-option__stop-indicator--outline-black': variant === 'contrast',
			})}
		/>
	);
}

function StopDepartureIndicator({ variant, useMaterialIcons }: StopIndicatorProps) {
	return (
		<div
			className={classNames('leg-option__stop-indicator-symbol leg-option__stop-indicator-symbol--start', {
				'leg-option__stop-indicator-symbol--solid-white': variant === 'white',
				'leg-option__stop-indicator-symbol--solid-primary': variant === 'contrast',
			})}
		>
			<Icon name={useMaterialIcons ? 'directions_boat' : 'ship'} useMaterialIcons={useMaterialIcons} />
		</div>
	);
}

function StopDestinationIndicator({ variant, useMaterialIcons }: StopIndicatorProps) {
	return (
		<div
			className={classNames('leg-option__stop-indicator-symbol leg-option__stop-indicator-symbol--end', {
				'leg-option__stop-indicator-symbol--solid-white': variant === 'white',
				'leg-option__stop-indicator-symbol--solid-primary': variant === 'contrast',
			})}
		>
			<Icon name={useMaterialIcons ? 'location_on' : 'location-pin'} useMaterialIcons={useMaterialIcons} />
		</div>
	);
}

interface RouteLineProps {
	variant?: 'white' | 'contrast';
	isSegment?: boolean;
	hasArrow?: 'right';
}

function RouteLine({ variant, isSegment }: RouteLineProps) {
	return (
		<div
			className={classNames('leg-option__route-line', {
				'leg-option__route-line--segment': isSegment,
				'leg-option__route-line--white': variant === 'white',
			})}
		/>
	);
}
